import React, {useContext, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {Avatar, Button, Checkbox, message, Select} from 'antd'
import {BsPersonFill} from 'react-icons/bs'
import {RiSearch2Line} from 'react-icons/ri'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import UserLookupComponent from 'components/UserLookupComponent'
import getAvatar from 'helpers/getAvatar'
import getTitle from 'helpers/getTitle'
import {getId} from 'apis/model/base'
import {
    organization_getFeatureAccessGrants_Api,
    organization_grantFeatureAccess_Api,
    organization_revokeFeatureAccessGrant_Api,
} from 'apis/organization'
import WorkspaceContext from '../WorkspaceContext'
import LoadingBlock from '../../Loading/LoadingBlock'

// "Cấp quyền tạm thời" (feature_access_grants) - GENERIC cho MỌI feature_key (không riêng Sổ Gia
// Đình), 1 màn DUY NHẤT quản lý hết ở Thiết lập hệ thống - xem comment đầu
// Modules\Organization\Http\Controllers\FeatureAccessGrantController. Người được cấp thao tác NHƯ
// admin thật nhưng CHỈ trong đúng feature đã cấp (xem DataHelper::isAdminOrGranted() - áp dụng tại
// từng module, không đổi gì ở đây). Cấp/thu hồi CHỈ admin THẬT làm được (kể cả khi FE này lỡ hiện ra
// cho người khác, BE tự chặn lại).
const EXPIRY_OPTIONS = [
    {value: '', label: 'unlimited access'},
    {value: '7', label: '7 days'},
    {value: '30', label: '30 days'},
    {value: '90', label: '90 days'},
]

const GrantRow = ({item, onRevoke, revoking, t}) => (
    <div className="p-3 flex items-center gap-3 background border border-color-100 rounded-xl">
        <Avatar
            size={36}
            src={getAvatar(item.user)}
            icon={<BsPersonFill size={16} className="text-color-500"/>}
            className="flex-shrink-0 flex flex-center background-200"
        />
        <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-color-000 truncate">{getTitle(item.user)}</div>
            <div className="text-xs text-color-400 truncate">{item.feature_label}</div>
            <div className="text-2xs text-color-300">
                {t('granted by')} {getTitle(item.granted_by_user)} · {dayjs(item.granted_at).format('DD/MM/YYYY')}
                {item.expires_at
                    ? ` · ${t('expires on')} ${dayjs(item.expires_at).format('DD/MM/YYYY')}`
                    : ` · ${t('unlimited access')}`}
            </div>
        </div>
        {item.is_active ? (
            <button
                type="button"
                disabled={revoking}
                onClick={() => onRevoke(item.id)}
                className="px-2.5 py-1.5 flex-shrink-0 rounded-lg text-2xs font-semibold text-rose-600 hover:bg-rose-50 cursor-pointer disabled:opacity-50">
                {t('revoke access grant')}
            </button>
        ) : (
            <span className="px-2 py-1 flex-shrink-0 rounded-md text-2xs font-semibold text-color-300 bg-color-50">
                {item.status === 0 ? t('access grant revoked') : t('expired')}
            </span>
        )}
    </div>
)

const WorkspaceFeatureAccessGrants = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const organizationId = getId(organization)
    const [refreshCount, setRefreshCount] = useState(0)
    const [featureKey, setFeatureKey] = useState(null)
    const [expiresInDays, setExpiresInDays] = useState('')
    const [includeRevoked, setIncludeRevoked] = useState(false)

    const {response, isLoading} = useAsync({
        apiInfo: organization_getFeatureAccessGrants_Api,
        query: {id: organizationId},
        values: {include_revoked: includeRevoked},
        deps: [organizationId, includeRevoked, refreshCount],
    })
    const features = _.get(response, 'data.features', {})
    const grants = _.get(response, 'data.data', [])
    const featureOptions = Object.entries(features).map(([value, label]) => ({value, label}))

    // featureKey mặc định = lựa chọn đầu tiên khi danh sách vừa load xong (tránh Select rỗng lúc
    // đầu, KHÔNG dùng useEffect chỉ để set 1 lần - tính thẳng ở đây khi featureKey chưa có).
    const effectiveFeatureKey = featureKey || featureOptions[0]?.value || null

    const {handleAsyncAction: doGrant, loading: granting} = useAsyncAction({
        apiInfo: organization_grantFeatureAccess_Api,
        onSuccess: () => {
            message.success(t('access granted'))
            setRefreshCount((c) => c + 1)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    const {handleAsyncAction: doRevoke, loading: revoking} = useAsyncAction({
        apiInfo: organization_revokeFeatureAccessGrant_Api,
        onSuccess: () => {
            message.success(t('access revoked'))
            setRefreshCount((c) => c + 1)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <span className="font-bold text-lg text-color-000">{t('temporary access grants')}</span>
                <span className="text-sm text-color-400 leading-snug">{t('temporary access grants hint')}</span>
            </div>

            <div className="p-3 flex flex-col gap-2.5 background border border-color-100 rounded-xl">
                <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('grant new access')}</span>
                <div className={classNames('grid gap-2', 'grid-cols-1 sm:grid-cols-2')}>
                    <Select
                        size="large"
                        placeholder={t('choose feature')}
                        value={effectiveFeatureKey}
                        onChange={setFeatureKey}
                        options={featureOptions}
                    />
                    <Select
                        size="large"
                        value={expiresInDays}
                        onChange={setExpiresInDays}
                        options={EXPIRY_OPTIONS.map((o) => ({value: o.value, label: t(o.label)}))}
                    />
                </div>
                <UserLookupComponent
                    handleApply={(userInfo) => {
                        if (!effectiveFeatureKey) {
                            message.error(t('choose feature first'))
                            return
                        }
                        if (userInfo?.id) {
                            doGrant(
                                {user_id: userInfo.id, feature_key: effectiveFeatureKey, expires_in_days: expiresInDays || undefined},
                                {id: organizationId}
                            )
                        }
                    }}>
                    <Button
                        size="large" type="primary" loading={granting}
                        icon={<RiSearch2Line size={14}/>}
                        className="w-full flex flex-center gap-1.5">
                        {t('find user to grant access')}
                    </Button>
                </UserLookupComponent>
            </div>

            <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('active grants')}</span>
                <Checkbox checked={includeRevoked} onChange={(e) => setIncludeRevoked(e.target.checked)}>
                    <span className="text-xs text-color-400">{t('show revoked history')}</span>
                </Checkbox>
            </div>

            {isLoading && !grants.length ? (
                <div className="py-6 flex flex-center"><LoadingBlock/></div>
            ) : !grants.length ? (
                <span className="text-sm text-color-300 italic text-center py-6">{t('no grants yet')}</span>
            ) : (
                <div className="flex flex-col gap-2">
                    {grants.map((item) => (
                        <GrantRow
                            key={item.id} item={item} t={t} revoking={revoking}
                            onRevoke={(grantId) => doRevoke({}, {id: organizationId, grant_id: grantId})}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default WorkspaceFeatureAccessGrants
